import React, { useState, useEffect } from 'react';
import {
  RaRecord,
  useRecordContext,
  TextField,
  Datagrid,
  ArrayField,
  useNotify,
} from 'react-admin';
import * as R from 'ramda';
import PdfButton from 'components/PdfButton';
import GetApp from '@mui/icons-material/GetApp';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import { formatDate } from 'utils/date';
import LinkField from 'components/LinkField';
import CredentialsFilter, { FilterEnum } from './CredentialsFilter';
import { Link } from 'react-router-dom';
import useGetPDF from 'hooks/useGetPDF';

const statusEq = R.propEq('status');

const color = R.cond<any, string>([
  [statusEq('review'), R.always('#5188ec')],
  [R.propEq('expired', true), R.always('#e65f4c')],
  [statusEq('approved'), R.always('#54ca64')],
  [statusEq('rejected'), R.always('#e65f4c')],
  [statusEq(undefined), R.always('#e65f4c')],
  [R.T, R.always('#E6EBEC')],
]);

const credentialRowStyle = (record: RaRecord) => ({
  borderLeft: '4px solid',
  borderColor: color(record),
  textDecoration: 'none',
});

const EmptyCredentialRow = () => (
  <TableRow>
    <TableCell
      sx={{ textAlign: 'center', paddingY: 8 }}
      colSpan={9}
      data-testid="professional_show-credentials_no_records"
    >
      No records found
    </TableCell>
  </TableRow>
);

const CredentialTable = ({ data }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell data-testid="professional_show-credentials_id_header">ID</TableCell>
        <TableCell data-testid="professional_show-credentials_credential_header">
          Credential
        </TableCell>
        <TableCell data-testid="professional_show-credentials_type_header">Type</TableCell>
        <TableCell data-testid="professional_show-credentials_state_header">State</TableCell>
        <TableCell data-testid="professional_show-credentials_original_issue_date_header">
          Original Issue Date
        </TableCell>
        <TableCell data-testid="professional_show-credentials_expires_header">Expires</TableCell>
        <TableCell data-testid="professional_show-credentials_status_header">Status</TableCell>
        <TableCell data-testid="professional_show-credentials_created_at_header">
          Created At
        </TableCell>
        <TableCell data-testid="professional_show-credentials_updated_at_header">
          Updated At
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {!data.length && <EmptyCredentialRow />}
      {data.map((row, index) => (
        <TableRow key={index} style={credentialRowStyle(row)}>
          <TableCell>
            {row.id && (
              <LinkField OVERRIDE_resource="ProfessionalQualification" source="id" record={row}>
                {row.id}
              </LinkField>
            )}
          </TableCell>
          <TableCell data-testid="professional_show-credentials_credential_field">
            {row?.long_name}
          </TableCell>
          <TableCell data-testid="professional_show-credentials_type_field">
            {row?.qualification_type}
          </TableCell>
          <TableCell data-testid="professional_show-credentials_state_field">
            {
              row?.professional_qualification_attributes?.find((attribute) => !!attribute.state)
                ?.state
            }
          </TableCell>
          <TableCell data-testid="professional_show-credentials_original_issue_date_field">
            {formatDate(
              row?.professional_qualification_attributes?.find((attribute) =>
                attribute.qualification_attribute?.slug.includes('original_issue_date'),
              )?.value,
            )}
          </TableCell>
          <TableCell data-testid="professional_show-credentials_expires_field">
            {formatDate(
              row?.professional_qualification_attributes?.find(({ type }) =>
                type.includes('ExpirationDate'),
              )?.value,
            )}
          </TableCell>
          <TableCell data-testid="professional_show-credentials_status_field">
            <Typography color={row.status ? 'inherit' : 'error'}>
              {row.status ?? 'Missing'}
            </Typography>
          </TableCell>
          <TableCell data-testid="professional_show-credentials_created_at_field">
            {formatDate(row?.created_at)}
          </TableCell>
          <TableCell data-testid="professional_show-credentials_updated_at_field">
            {formatDate(row?.updated_at)}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const EmptyConditionsTable = () => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Id</TableCell>
        <TableCell>Label</TableCell>
        <TableCell>Condition Type</TableCell>
        <TableCell>Payload</TableCell>
      </TableRow>
    </TableHead>
    <EmptyCredentialRow />
  </Table>
);

const SatisfiedConditionsTable = () => (
  <ArrayField source="satisfied_conditions">
    <Datagrid bulkActionButtons={false} empty={<EmptyConditionsTable />}>
      <TextField source="id" />
      <TextField source="label" />
      <TextField source="condition_type" />
      <TextField source="payload" />
    </Datagrid>
  </ArrayField>
);

export const CredentialsTab = () => {
  const [currentFilter, setCurrentFilter] = useState<FilterEnum>(FilterEnum.ALL);
  const [credentialsData, setCredentialsData] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const getPDF = useGetPDF();
  const notify = useNotify();

  const record = useRecordContext();
  if (!record) {
    return null;
  }

  const createFileName = () => {
    const firstName = record?.account?.first_name ?? '';
    const lastName = record?.account?.last_name ?? '';

    return firstName && lastName
      ? `${lastName}.${firstName}.compliance_package.pdf`
      : 'unnamed.compliance_package.pdf';
  };

  const handleDownloadAllCredentials = async () => {
    const credentials = credentialsData?.map((credential) => ({
      id: credential.id,
      long_name: credential.long_name,
    }));

    if (!credentials?.length) {
      notify('No credentials available to download', { type: 'warning' });
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(0);

    const firstName = record?.account?.first_name ?? '';
    const lastName = record?.account?.last_name ?? '';
    const professionalName = firstName && lastName ? `${firstName} ${lastName}` : 'Unknown';

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < credentials.length; i++) {
      const credential = credentials[i];
      const fileName = `${professionalName}_${credential.long_name}.pdf`;

      try {
        await getPDF({ url: `/professional_qualifications/${credential.id}` }, fileName);
        successCount++;
      } catch (error) {
        // Log error but continue with next credential
        console.error(`Failed to download credential: ${credential.long_name}`, error);
        failedCount++;
      }

      setDownloadProgress(Math.round(((i + 1) / credentials.length) * 100));

      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    if (failedCount === 0) {
      notify('All credentials downloaded successfully', { type: 'success' });
    } else if (successCount > 0) {
      notify(`Downloaded ${successCount} credentials, ${failedCount} failed`, { type: 'warning' });
    } else {
      notify('Failed to download any credentials', { type: 'error' });
    }

    setIsDownloading(false);
    setDownloadProgress(0);
  };

  const viewingSatisfiedConditions = currentFilter === FilterEnum.SATISFIED_CONDITIONS;
  const isAgencyProfessional = Boolean(record?.account?.agency_id);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, marginY: 1 }}>
        {!isAgencyProfessional && (
          <PdfButton
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<GetApp />}
            download={createFileName()}
            href={`/professionals/${record?.id}/compliance_package`}
            sx={{ width: 205 }}
          >
            Compliance Package
          </PdfButton>
        )}
        {credentialsData.length > 0 && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={isDownloading ? <CircularProgress size={22} /> : <GetApp />}
            onClick={handleDownloadAllCredentials}
            disabled={isDownloading}
            sx={{ width: 205 }}
            data-testid="download-credentials-button"
          >
            {isDownloading ? `Downloading ${downloadProgress}%` : 'Credentials'}
          </Button>
        )}
      </Box>
      <CredentialsFilter currentFilter={currentFilter} setCurrentFilter={setCurrentFilter}>
        {({ data }) => {
          useEffect(() => {
            if (!credentialsData?.length) {
              setCredentialsData(data || []);
            }
          }, [data]);

          return viewingSatisfiedConditions ? (
            <SatisfiedConditionsTable />
          ) : (
            <CredentialTable data={data} />
          );
        }}
      </CredentialsFilter>
      <Button
        component={Link}
        to="/ProfessionalQualification/create"
        state={{ record: { professional_id: record.id } }}
        variant="contained"
        size="small"
        sx={{ width: 205, marginTop: '16px' }}
        data-testid="professional_show-add_credentials_button"
      >
        Add Credentials
      </Button>
    </>
  );
};

export default CredentialsTab;
